import {
  faAngleDown,
  faAngleRight,
  faAngleUp,
  faArrowRight,
  faCalendarDays,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { i18n } from "../../App";
import DateFormat from "../../components/DateFormat";
import Button from "../../components/Button";
import LoadingContent from "../../components/LoadingContent"; // <-- Importa tu componente de carga aquí (ajusta la ruta si es necesario)

interface NewsItem {
  title: string;
  id: number;
  image: string;
  championship: string;
  date: string;
  caption: string;
}

const News = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  document.title = `LVF1 - ${t("News")}`;

  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true); // <-- Estado de carga añadido

  const [championships, setChampionships] = useState<
    { name: string; id: number }[]
  >([]);

  const [news, setNews] = useState<NewsItem[]>([]);

  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState<(string | number)[]>([]);

  const [championship, setChampionship] = useState("");
  const [searchText, setSearchText] = useState("");
  const [reportOnly, setReportOnly] = useState(false);
  const [dates, setDates] = useState<any[]>([null, null]);

  /*
   * Lo utilizamos para lanzar la búsqueda.
   * Así no tenemos que depender de cambiar el valor de un boolean.
   */
  const [searchVersion, setSearchVersion] = useState(0);

  useEffect(() => {
    setLoading(true); // <-- Activamos la carga antes de la petición
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/news/getLatestNews`,
        {
          params: {
            language: i18n.language,
            page,
            dates,
            reportOnly,
            championship,
            searchText,
          },
        },
      )
      .then(({ data }) => {
        setChampionships(data.champs);
        setTotalPages(data.totalPages);
        setNews(data.news);

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      })
      .catch(({ response }) => {
        toast.error(response?.data?.message || "Error loading news");
      })
      .finally(() => {
        setLoading(false); // <-- Desactivamos la carga al terminar (éxito o error)
      });
  }, [page, i18n.language, searchVersion]);

  /*
   * Paginación
   */
  useEffect(() => {
    setPagination(getPagination(page, totalPages));
  }, [page, totalPages]);

  function getPagination(current: number, total: number): (string | number)[] {
    const pages: (string | number)[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }

      return pages;
    }

    pages.push(1);

    if (current > 3) {
      pages.push("...");
    }

    for (let i = current - 1; i <= current + 1; i++) {
      if (i > 1 && i < total) {
        pages.push(i);
      }
    }

    if (current < total - 2) {
      pages.push("...");
    }

    pages.push(total);

    return pages;
  }

  /*
   * Ejecutar búsqueda
   */
  const executeSearch = () => {
    setPage(1);
    setSearchVersion((value) => value + 1);
    setShowFilters(false);
  };

  /*
   * Limpiar filtros
   */
  const clearFilters = () => {
    setChampionship("");
    setSearchText("");
    setReportOnly(false);
    setDates([null, null]);

    setPage(1);
    setSearchVersion((value) => value + 1);
    setShowFilters(false);
  };

  /*
   * Navegar a una noticia
   */
  const openNews = (id: number) => {
    navigate(`${t("routes:News")}/LVF1/${id}`);
  };

  /*
   * Primera noticia como destacada
   */
  const featuredNews = news.length > 0 && page === 1 ? news[0] : null;

  /*
   * Resto de noticias
   */
  const regularNews = news.slice(1);

  return (
    <div className="full-height news-page padding-md app-container flex-center-top page-transition">
      <div className="news-container">
        {/* =====================================================
            HEADER
        ===================================================== */}

        <header className="news-header">
          <div className="news-header-title">
            <h1>
              {t("News")} <span>LVF1</span>
            </h1>
          </div>

          {/* =====================================================
              SEARCH
          ===================================================== */}

          <div className="news-search-area">
            <div className="news-search-box">
              <FontAwesomeIcon icon={faMagnifyingGlass} />

              <input
                type="text"
                value={searchText}
                placeholder={t("SearchNews")}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    executeSearch();
                  }
                }}
              />

              <button
                type="button"
                className="news-search-button"
                onClick={executeSearch}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </button>

              <button
                type="button"
                className={`news-filter-button ${showFilters ? "active" : ""}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FontAwesomeIcon icon={faFilter} />

                <FontAwesomeIcon icon={showFilters ? faAngleUp : faAngleDown} />
              </button>
            </div>

            {/* =====================================================
                FILTERS
            ===================================================== */}

            {showFilters && (
              <div className="news-filters">
                <div className="news-filter-field">
                  <label>{t("Championship")}</label>

                  <select
                    value={championship}
                    onChange={(e) => setChampionship(e.target.value)}
                  >
                    <option value="">{t("All")}</option>

                    {championships.map((champ) => (
                      <option value={champ.name} key={champ.id}>
                        {champ.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="news-filter-field news-report-filter">
                  <label>{t("Reports Only")}</label>

                  <label className="news-checkbox">
                    <input
                      type="checkbox"
                      checked={reportOnly}
                      onChange={(e) => setReportOnly(e.target.checked)}
                    />

                    <span></span>

                    {t("Reports Only")}
                  </label>
                </div>

                <div className="news-filter-field news-date-field">
                  <label>{t("Date")}</label>

                  <div className="news-date-inputs">
                    <input
                      type="date"
                      value={dates[0] || ""}
                      onChange={(e) => {
                        const newDate = e.target.value;

                        setDates(([_, second]) => [newDate || null, second]);
                      }}
                    />

                    <FontAwesomeIcon icon={faAngleRight} />

                    <input
                      type="date"
                      value={dates[1] || ""}
                      onChange={(e) => {
                        const newDate = e.target.value;

                        setDates(([first, _]) => [first, newDate || null]);
                      }}
                    />
                  </div>
                </div>

                <div className="news-filter-actions">
                  <Button
                    text={t("Clear")}
                    onClick={clearFilters}
                    className="button-neutral-lvf1"
                  />

                  <Button text={t("Search")} onClick={executeSearch} />
                </div>
              </div>
            )}
          </div>
        </header>

        {/* =====================================================
            CONTENT / LOADING / NO NEWS
        ===================================================== */}

        {loading ? (
          <div className="full-height news-page padding-md app-container flex-center-top page-transition">
            <LoadingContent />
          </div>
        ) : news.length === 0 ? (
          <div className="news-empty">
            <FontAwesomeIcon icon={faMagnifyingGlass} />

            <h2>{t("NoNews")}</h2>

            <p>
              {t("NoNewsDescription") ||
                "No se han encontrado noticias con los filtros seleccionados."}
            </p>
          </div>
        ) : (
          <>
            {/* =================================================
                FEATURED NEWS
            ================================================= */}

            {featuredNews && (
              <article
                className="news-featured"
                onClick={() => openNews(featuredNews.id)}
              >
                <div className="news-featured-image">
                  <img src={featuredNews.image} alt={featuredNews.title} />

                  <div className="news-featured-overlay" />
                </div>

                <div className="news-featured-content">
                  <span className="news-featured-label">
                    {t("FeaturedNews")}
                  </span>

                  <DateFormat
                    date={featuredNews.date}
                    format="short_year"
                    disaggregated={true}
                  />

                  <h2>{featuredNews.title}</h2>

                  <p>{featuredNews.caption}</p>

                  <Button text={t("ExpandNews")} icon={faArrowRight} />
                </div>
              </article>
            )}

            {/* =================================================
                NEWS TIMELINE
            ================================================= */}

            {regularNews.length > 0 && (
              <section className="news-latest">
                <div className="news-section-title">
                  <div>
                    <FontAwesomeIcon icon={faCalendarDays} />
                    <span>{t("News")}</span>
                  </div>
                </div>

                <div className="news-timeline">
                  {regularNews.map((new_, index) => (
                    <article
                      key={new_.id}
                      className="news-timeline-item"
                      onClick={() => openNews(new_.id)}
                    >
                      {/* Fecha */}
                      <div className="news-timeline-date">
                        <DateFormat
                          date={new_.date}
                          format="short"
                          disaggregated={true}
                        />
                      </div>

                      {/* Punto */}
                      <div className="news-timeline-point">
                        <span />
                      </div>

                      {/* Card */}
                      <div className="news-card">
                        <div className="news-card-image">
                          <img src={new_.image} alt={new_.title} />
                        </div>

                        <div className="news-card-content">
                          <div className="news-card-meta">
                            {new_.championship && (
                              <span className="news-card-category">
                                {new_.championship}
                              </span>
                            )}

                            <DateFormat
                              date={new_.date}
                              format="short_year"
                              disaggregated={true}
                            />
                          </div>

                          <h3>{new_.title}</h3>

                          <p>{new_.caption}</p>
                        </div>

                        <div className="news-card-arrow">
                          <FontAwesomeIcon icon={faAngleRight} />
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* =================================================
                PAGINATION
            ================================================= */}

            <div className="news-pagination">
              <button
                type="button"
                className="pagination-arrow"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              {pagination.map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} className="pagination-ellipsis">
                    ...
                  </span>
                ) : (
                  <button
                    type="button"
                    key={`page-${i}`}
                    className={`pagination-page ${p === page ? "active" : ""}`}
                    onClick={() => {
                      setPage(Number(p));
                    }}
                    disabled={p === page}
                  >
                    {p}
                  </button>
                ),
              )}

              <button
                type="button"
                className="pagination-arrow"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default News;
